import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Button,
  Row,
  Table,
  Input,
  Col,
  Modal,
  Form,
  Tooltip,
  Select,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  QuestionCircleOutlined,
  DeleteOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import Highlighter from "react-highlight-words";
import Title from "components/Title/Title";
import "./contactInput.css";
import { withAppContext } from "contexts/AppContext/AppContext";
import update from "immutability-helper";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useFetchConstant } from "apis/constant";
import { getLabelOfConstant } from "utils/general";
import { buildManageEmergencyContactTemplateUri } from "utils/routes";
import Locale from "react-intl-universal";

const type = "DraggableBodyRow";

const DraggableBodyRow = ({
  index,
  moveRow,
  className,
  style,
  ...restProps
}) => {
  const ref = useRef(null);
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};

      if (dragIndex === index) {
        return {};
      }

      return {
        isOver: monitor.isOver(),
        dropClassName:
          dragIndex < index ? " drop-over-downward" : " drop-over-upward",
      };
    },
    drop: (item) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: {
      index,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));
  return (
    <tr
      ref={ref}
      className={`${className}${isOver ? dropClassName : ""}`}
      style={{
        cursor: "move",
        ...style,
      }}
      {...restProps}
    />
  );
};

const EmergencyContact = ({
  isAdmin,
  data,
  loading,
  handleUpdateGlobalDataOnChange,
  title,
  refetch,
}) => {
  const history = useHistory();
  const [form] = Form.useForm();
  const [emergencyContactVisible, setEmergencyContactVisible] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [isEditMode, setEditMode] = useState(false);
  const [editIndex, setEditIndex] = useState("");

  const { isLoading: contactTypeLoading, data: contactType } = useFetchConstant(
    "contactType"
  );

  const [roleSelection, setRoleSelection] = useState([]);

  useEffect(() => {
    if (Array.isArray(contactType)) {
      setRoleSelection(contactType);
    }
  }, [contactType]);

  const handleSubmitContact = async () => {
    const values = await form.validateFields();
    console.log(values);

    if (isEditMode) {
      // console.log("editmode");

      var buffer = data;

      buffer = buffer.map((x) => {
        if (x._id === editIndex) {
          return values;
        } else {
          return x;
        }
      });
      await handleUpdateGlobalDataOnChange([...buffer]);
    } else {
      await handleUpdateGlobalDataOnChange([values, ...data]);
    }

    setEmergencyContactVisible(false);
  };

  const handleDeleteContact = async (e) => {
    let buffer = [...data];
    buffer = buffer.filter((x) => JSON.stringify(x) !== JSON.stringify(e));
    await handleUpdateGlobalDataOnChange(buffer);
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={(node) => {
            setSearchInput(node);
          }}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Search
        </Button>
        <Button
          onClick={() => handleReset(clearFilters)}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          marginRight: 8,
          fontSize: 18,
          color: filtered ? "#ffc069" : undefined,
        }}
      />
    ),
    onFilter: (value, record) => {
      // console.log(record);
      if (record[dataIndex]) {
        return record[dataIndex]
          .toString()
          .toLowerCase()
          .includes(value.toLowerCase());
      } else {
        return false;
      }
    },
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput);
      }
    },
    render: (text) => {
      if (text) {
        return (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text.toString()}
          />
        );
      }
    },
  });

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
    setSearchText(selectedKeys[0]);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const columns = [
    {
      title: Locale.get("emergencyContact.emergencyComponent.role"),
      dataIndex: "role",
      key: "role",
      fixed: "left",
      sorter: (a, b) => a.role?.localeCompare(b.role),
      ...getColumnSearchProps("role"),
      render: (value) =>
        getLabelOfConstant(value, contactType, "code")
          ? getLabelOfConstant(value, contactType, "code")
          : value,
    },
    {
      title: Locale.get("emergencyContact.emergencyComponent.noc"),
      dataIndex: "nameOrCompany",
      key: "nameOrCompany",
      fixed: "left",
      sorter: (a, b) => a.nameOrCompany?.localeCompare(b.nameOrCompany),
      ...getColumnSearchProps("nameOrCompany"),
    },
    {
      title: Locale.get("emergencyContact.emergencyComponent.email"),
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email?.localeCompare(b.email),
      ...getColumnSearchProps("email"),
      render: (v) => (v ? v : "-"),
    },
    {
      title: Locale.get("emergencyContact.emergencyComponent.website"),
      dataIndex: "website",
      key: "website",
      sorter: (a, b) => a.website?.localeCompare(b.website),
      ...getColumnSearchProps("website"),
      render: (v) => (v ? v : "-"),
    },
    {
      title: Locale.get("emergencyContact.emergencyComponent.contactNo"),
      dataIndex: "contact",
      key: "contact",
      sorter: (a, b) => a.contact?.localeCompare(b.contact),
      ...getColumnSearchProps("contact"),
    },
    {
      title: Locale.get("emergencyContact.emergencyComponent.officeNo"),
      dataIndex: "officeNumber",
      key: "officeNumber",
      sorter: (a, b) => a.officeNumber?.localeCompare(b.officeNumber),
      ...getColumnSearchProps("officeNumber"),
      render: (v) => (v ? v : "-"),
    },
    {
      title: Locale.get("emergencyContact.emergencyComponent.faxNo"),
      dataIndex: "faxNumber",
      key: "faxNumber",
      sorter: (a, b) => a.faxNumber?.localeCompare(b.faxNumber),
      ...getColumnSearchProps("faxNumber"),
      render: (v) => (v ? v : "-"),
    },
    {
      title: Locale.get("emergencyContact.emergencyComponent.action"),
      render: (record, value, index, data) => {
        // console.log("index", record);

        return (
          <Row>
            <Col>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => {
                  setEditMode(true);
                  // setEditIndex(index);
                  setEditIndex(record._id);
                  setEmergencyContactVisible(true);
                  setTimeout(() => {
                    form.setFieldsValue(record);
                  }, 100);
                }}
              >
                {Locale.get("emergencyContact.emergencyComponent.edit")}
              </Button>
            </Col>
            <Col>
              <Button
                danger
                type="link"
                icon={<DeleteOutlined />}
                onClick={() =>
                  Modal.confirm({
                    title: Locale.get("emergencyContact.deleteContact"),
                    okText: (
                      <span>
                        <DeleteOutlined />{" "}
                        {Locale.get("emergencyContact.delete")}
                      </span>
                    ),
                    okType: "danger",
                    onOk: () => handleDeleteContact(record),
                  })
                }
              >
                {Locale.get("emergencyContact.emergencyComponent.delete")}
              </Button>
            </Col>
          </Row>
        );
      },
    },
  ];

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      md: { span: 7 },
      xl: { span: 7 },
    },
    wrapperCol: {
      xs: { span: 24 },
      md: { span: 16 },
      xl: { span: 16 },
    },
  };

  const AddEmergencyContactModal = (
    <Modal
      visible={emergencyContactVisible}
      title={
        isEditMode
          ? "Edit Emergency Contact"
          : Locale.get("emergencyContact.addEmergency")
      }
      afterClose={() => form.resetFields()}
      onCancel={() => {
        setEmergencyContactVisible(false);
      }}
      onOk={handleSubmitContact}
      okButtonProps={{ loading: loading }}
      cancelButtonProps={{ disabled: loading }}
    >
      <div>
        <Form
          {...formItemLayout}
          form={form}
          // hideRequiredMark
          colon={true}
          name="registration"
          scrollToFirstError
          // onFinish={onFinish}
        >
          <Form.Item
            label={Locale.get("emergencyContact.emergencyComponent.role")}
            name="role"
            rules={[
              {
                required: true,
                message: Locale.get(
                  "emergencyContact.emergencyComponent.errMsgRole"
                ),
              },
            ]}
            hasFeedback
          >
            <Select
              showSearch
              onSearch={(e) => {
                console.log(e, 364);

                setRoleSelection([
                  {
                    code: e,
                    label: e,
                  },
                  ...contactType,
                ]);
              }}
            >
              {!contactTypeLoading &&
                roleSelection.map((selection) => (
                  <Select.Option
                    value={selection.code}
                    key={selection.code}
                    style={{ background: selection.code }}
                  >
                    {selection.label}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={Locale.get("emergencyContact.emergencyComponent.noc")}
            name="nameOrCompany"
            rules={[
              {
                required: true,
                message: Locale.get(
                  "emergencyContact.emergencyComponent.errMsgNoc"
                ),
              },
            ]}
            hasFeedback
          >
            <Input disabled={loading} placeholder="Finport Sdn Bhd" />
          </Form.Item>

          <Form.Item
            name="email"
            label={Locale.get("emergencyContact.emergencyComponent.email")}
            rules={[
              {
                required: false,
                message: Locale.get(
                  "emergencyContact.emergencyComponent.errMsgEmail"
                ),
              },
              {
                type: "email",
              },
            ]}
          >
            <Input disabled={loading} placeholder="finport@example.com" />
          </Form.Item>

          <Form.Item
            label={Locale.get("emergencyContact.emergencyComponent.website")}
            name="website"
            hasFeedback
            rules={[
              {
                required: false,
                message: Locale.get(
                  "emergencyContact.emergencyComponent.errMsgWebsite_a"
                ),
              },
              {
                type: "url",
                message: Locale.get(
                  "emergencyContact.emergencyComponent.errMsgWebsite_b"
                ),
              },
            ]}
          >
            <Input
              disabled={loading}
              placeholder="https://www.finport.com"
              type="url"
            />
          </Form.Item>

          <Form.Item
            label={Locale.get("emergencyContact.emergencyComponent.contact")}
            hasFeedback
            name="contact"
            rules={[
              {
                required: false,
                message: Locale.get(
                  "emergencyContact.emergencyComponent.errMsgContact"
                ),
              },
            ]}
          >
            <Input
              onWheel={(e) => e.target.blur()}
              disabled={loading}
              type="number"
              placeholder="0124567890"
            />
          </Form.Item>

          <Form.Item
            label={Locale.get("emergencyContact.emergencyComponent.officeNo")}
            hasFeedback
            name="officeNumber"
          >
            <Input
              onWheel={(e) => e.target.blur()}
              disabled={loading}
              type="number"
              placeholder="041234567"
            />
          </Form.Item>

          <Form.Item
            label={Locale.get("emergencyContact.emergencyComponent.faxNo")}
            hasFeedback
            name="faxNumber"
          >
            <Input
              onWheel={(e) => e.target.blur()}
              disabled={loading}
              type="number"
              placeholder="041234567"
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );

  const moveRow = useCallback(
    async (dragIndex, hoverIndex) => {
      const dragRow = data[dragIndex];

      await handleUpdateGlobalDataOnChange(
        update(data, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragRow],
          ],
        })
      );
    },
    [data]
  );

  return (
    <div>
      <div>
        {/* <Switch> */}
        {/* <Route exact path={buildEmergencyContactUri() || buildSettingUri()}> */}
        <div>
          <Row
            justify="space-between"
            align="middle"
            style={{ marginBottom: 32 }}
          >
            <Title>
              <Tooltip
                placement="bottomRight"
                title={
                  <div>
                    <img src="/emergency-contact-sample.png" width={480} />
                  </div>
                }
              >
                {title ? title : Locale.get("emergencyContact.pageTitle")}{" "}
                <QuestionCircleOutlined />
              </Tooltip>
            </Title>

            <Row gutter={24}>
              <Col>
                {isAdmin && !title && (
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() =>
                      history.push(buildManageEmergencyContactTemplateUri())
                    }
                    style={{ marginLeft: 24 }}
                  >
                    {Locale.get("emergencyContact.manageTemplate")}
                  </Button>
                )}
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setEditMode(false);
                    setEmergencyContactVisible(true);
                    // history.push(buildPolicyNewUri());
                  }}
                >
                  {Locale.get("emergencyContact.addEmergency")}
                </Button>
              </Col>
            </Row>
          </Row>
          <DndProvider backend={HTML5Backend}>
            <Table
              size="large"
              rowKey="_id"
              columns={columns}
              loading={loading}
              dataSource={data}
              scroll={{ scrollToFirstRowOnChange: true, x: true }}
              components={{
                body: {
                  row: DraggableBodyRow,
                },
              }}
              onRow={(_, index) => {
                const attr = {
                  index,
                  moveRow,
                };
                return attr;
              }}
            />
          </DndProvider>
        </div>

        {AddEmergencyContactModal}
        {/* </Route> */}
        {/* </Switch> */}
      </div>
    </div>
  );
};

export default withAppContext(EmergencyContact);
